import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Check, CornerDownRight } from 'lucide-react';
import { SectionHead, CtaButton, SCROLL_MT } from './shared';
import { track } from '../../lib/analytics';

function PainRow({ item, index, checked, onToggle }) {
  return (
    <motion.li initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: '-30px' }} transition={{ duration: 0.4, delay: index * 0.04 }}>
      <button type="button" role="checkbox" aria-checked={checked} onClick={onToggle} data-testid={`pain-row-${index}`}
        className={`w-full text-left rounded-xl border px-3.5 sm:px-4 py-2.5 sm:py-3 transition-all duration-300 ${checked ? 'bg-white border-emerald-300 shadow-[0_14px_30px_-20px_rgba(16,185,129,0.6)]' : 'bg-white/70 border-neutral-200 hover:border-slate-400 hover:bg-white'}`}>
        <div className="flex items-start gap-3">
          <span className={`mt-0.5 w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 transition-all duration-300 ${checked ? 'bg-emerald-500 border-emerald-500 text-white' : 'border-slate-300 bg-white text-transparent'}`}>
            <Check size={12} strokeWidth={3} />
          </span>
          <span className={`text-[14px] sm:text-[15px] font-semibold leading-snug ${checked ? 'text-slate-900' : 'text-slate-700'}`}>{item.pain}</span>
        </div>
        <AnimatePresence initial={false}>
          {checked && item.fix && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.25 }} className="overflow-hidden">
              <p className="mt-2 ml-8 flex items-start gap-1.5 text-[13px] text-emerald-800 leading-snug" data-testid={`pain-fix-${index}`}>
                <CornerDownRight size={14} className="shrink-0 mt-0.5 text-emerald-500" />
                <span><span className="font-bold">CELPIPMax fix:</span> {item.fix}</span>
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </button>
    </motion.li>
  );
}

export default function PainChecklist({ pains, onCta }) {
  const items = (pains.items || []).filter(i => i && i.pain);
  const [ticked, setTicked] = useState(() => new Set());
  const threshold = Math.max(1, Number(pains.threshold) || 2);
  const hit = ticked.size >= threshold;

  const toggle = (i) => {
    setTicked(prev => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i); else { next.add(i); track('pain_tick', { index: i }); }
      return next;
    });
  };

  return (
    <section id="sounds-like-you" className={`${SCROLL_MT} py-12 sm:py-16 lg:py-20 bg-white/40 border-y border-white`} data-testid="pains-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <SectionHead label={pains.label} title={pains.title} subtitle={pains.subtitle} className="mb-7 sm:mb-9" />
        <div className="grid lg:grid-cols-[minmax(0,7fr)_minmax(0,5fr)] gap-6 lg:gap-10 items-start">
          <div>
            <ul className="space-y-2" data-testid="pains-list">
              {items.map((item, i) => <PainRow key={i} item={item} index={i} checked={ticked.has(i)} onToggle={() => toggle(i)} />)}
            </ul>
            <div className="mt-3.5 flex items-center justify-center lg:justify-start gap-3" data-testid="pains-counter">
              <div className="flex gap-1">
                {items.map((_, i) => <span key={i} className={`h-1.5 w-5 rounded-full transition-colors duration-300 ${i < ticked.size ? 'bg-emerald-500' : 'bg-slate-200'}`} />)}
              </div>
              <span className="text-[12px] font-bold text-slate-500 tabular-nums">{ticked.size} / {items.length}</span>
            </div>
          </div>

          <motion.div animate={hit ? { scale: [1, 1.02, 1] } : { scale: 1 }} transition={{ duration: 0.45 }}
            className={`lg:sticky lg:top-28 relative overflow-hidden rounded-[22px] bg-slate-900 text-white p-6 sm:p-7 transition-shadow duration-500 ${hit ? 'shadow-[0_0_0_5px_rgba(245,158,11,0.25),0_30px_60px_-30px_rgba(15,23,42,0.7)]' : 'shadow-[0_24px_60px_-30px_rgba(15,23,42,0.6)]'}`}
            data-testid="pains-cta-card" data-state={hit ? 'hit' : 'idle'}>
            <span className={`pointer-events-none absolute -top-16 -right-16 w-44 h-44 rounded-full blur-3xl transition-opacity duration-700 ${hit ? 'bg-amber-400/40 opacity-100' : 'bg-blue-400/20 opacity-60'}`} aria-hidden="true" />
            <AnimatePresence mode="wait" initial={false}>
              <motion.div key={hit ? 'hit' : 'idle'} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.25 }}>
                <p className="font-[Manrope] font-extrabold text-[22px] sm:text-2xl leading-tight tracking-[-0.02em]" data-testid="pains-cta-title">{hit ? pains.cta_title : pains.cta_idle_title}</p>
                <p className={`mt-1.5 text-[14px] ${hit ? 'text-amber-300 font-semibold' : 'text-white/65'}`} data-testid="pains-cta-subtitle">{hit ? pains.cta_subtitle : pains.cta_idle_subtitle}</p>
              </motion.div>
            </AnimatePresence>
            <div className="mt-5">
              <CtaButton label={pains.cta_label || 'Get Instant Access'} onClick={() => onCta('sounds_like_you')} variant={hit ? 'amber' : 'blue'} size="md" className="w-full" testId="pains-cta" />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
